import { Link, useNavigate } from '@tanstack/react-router';
import { Archive, BookOpen, Pencil, Plus } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import type { AssessmentSummaryView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Popover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { formatDate } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useArchiveTest, useTests } from '../api/assessments-api';
import { AssessmentsHeader } from '../components/AssessmentsHeader';
import styles from '../components/Assessments.module.css';

/**
 * ASM-02's list (F9.02). Each edit saves a new version; a job keeps the version it
 * chose until someone picks the newer one in its setup (ADR 0019).
 */
export function TestsPage(): ReactNode {
  const canView = useCanView('assessment_template');
  const canEdit = useCanEdit('assessment_template');
  const tests = useTests();
  const archive = useArchiveTest();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const isExamSessionsOn = useModule('exam_sessions');
  const [archiving, setArchiving] = useState<{ readonly test: AssessmentSummaryView; readonly anchor: AnchorRect } | null>(null);

  if (!canView) return <ForbiddenState resource="tests" contact="Your admin" />;

  const open = (testId: string): void => void navigate({ to: '/assessments/tests/$testId', params: { testId } });
  const newButton = canEdit ? (
    <Button variant="solid" size="sm" icon={Plus} onClick={() => open('new')}>
      New test
    </Button>
  ) : undefined;

  // The row leaves the moment Archive is pressed; a refusal puts it back and says why (CLAUDE.md §5a).
  function confirmArchive(test: AssessmentSummaryView): void {
    setArchiving(null);
    archive.mutate(
      { id: test.id, version: test.version },
      {
        onSuccess: () => show({ tone: 'success', message: `${test.name} archived`, detail: test.jobsUsing > 0 ? 'Jobs already using it keep it.' : undefined }),
        onError: (cause) => show({ tone: 'danger', message: "The test wasn't archived", detail: errorMessage(cause) }),
      },
    );
  }

  return (
    <div className={styles.screen}>
      <AssessmentsHeader current="tests" />
      <Card
        title="Tests"
        meta="Built from the question bank. A job chooses one in its setup, step 5"
        action={
          <div className={styles.itemActions}>
            <Button variant="ghost" size="sm" icon={BookOpen} onClick={() => void navigate({ to: '/assessments/questions' })}>
              Question bank
            </Button>
            {newButton}
          </div>
        }
      >
        {tests.isPending ? (
          <SkeletonLines count={4} />
        ) : (tests.data ?? []).length === 0 ? (
          <EmptyState isInset title="No tests yet" body="Put questions in the bank first, then build a test from them." action={newButton} />
        ) : (
          <ul className={styles.list}>
            {(tests.data ?? []).map((test) => (
              <li key={test.id} className={styles.item}>
                <div className={styles.itemHead}>
                  <Link to="/assessments/tests/$testId" params={{ testId: test.id }} className={styles.itemName}>
                    {test.name}
                  </Link>
                  <Chip tone="neutral">Version {test.versionNumber}</Chip>
                  {/* "On paper" belongs to Exam sessions: switched off, the chip goes with it (#13, §L28). */}
                  {isExamSessionsOn && test.isOffline && (
                    <Chip tone="progress" plain>
                      On paper
                    </Chip>
                  )}
                  {test.isFullyAutoMarked ? (
                    <Chip tone="success" plain>
                      Marked by the system
                    </Chip>
                  ) : (
                    <Chip tone="attention" plain>
                      Has written answers
                    </Chip>
                  )}
                  <span className={styles.meta}>
                    {test.ventureName ? `${test.ventureName} · ` : ''}used by {test.jobsUsing} job{test.jobsUsing === 1 ? '' : 's'} · updated {formatDate(test.updatedAt)}
                  </span>
                  {canEdit && (
                    <div className={styles.itemActions}>
                      <Button size="sm" variant="ghost" icon={Pencil} onClick={() => open(test.id)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" icon={Archive} aria-label={`Archive ${test.name}`} onClick={(event: MouseEvent<HTMLButtonElement>) => setArchiving({ test, anchor: anchorFrom(event.currentTarget) })} />
                    </div>
                  )}
                </div>
                <p className={styles.route}>
                  {test.sectionCount} section{test.sectionCount === 1 ? '' : 's'} · {test.questionsGiven} question{test.questionsGiven === 1 ? '' : 's'} each · {test.totalPoints} marks · {test.durationMinutes} minutes · suggested pass mark {test.passPercent}%
                  {test.isShuffled ? ' · shuffled for each candidate' : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Popover anchor={archiving?.anchor ?? null} onClose={() => setArchiving(null)} align="right" label="Archive test">
        {archiving && (
          <div className={styles.confirm}>
            <p className={styles.confirmTitle}>Archive {archiving.test.name}?</p>
            <p className={styles.confirmBody}>
              {archiving.test.jobsUsing > 0 ? 'Jobs already sending it keep it, and so does every attempt. New jobs can no longer choose it.' : 'New jobs can no longer choose it.'}
            </p>
            <div className={styles.confirmActions}>
              <Button variant="ghost" size="sm" onClick={() => setArchiving(null)}>
                Keep it
              </Button>
              <Button variant="danger" size="sm" onClick={() => confirmArchive(archiving.test)}>
                Archive
              </Button>
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
}
